/**
 * Business logic for sharing job pages
 */

/**
 * Shares the current page using the Web Share API if available,
 * otherwise falls back to copying URL to clipboard
 * 
 * @param url - The URL to share
 * @param title - The title of the page
 * @param onSuccess - Callback when clipboard copy succeeds (not called when Web Share API is used)
 * @param onError - Callback when share fails
 */
export async function sharePage(
  url: string,
  title: string,
  onSuccess?: () => void,
  onError?: (error: Error) => void
): Promise<void> {
  if (typeof window === 'undefined') return;
  
  const shareData = {
    title,
    url,
  };
  
  try {
    // Try Web Share API first (mobile/desktop with native share)
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      await navigator.share(shareData);
      // Don't call onSuccess here - Web Share API handles its own UI
      return;
    }
  } catch (error: any) {
    // User cancelled or error occurred
    if (error.name !== 'AbortError') {
      onError?.(error);
    }
    return;
  }
  
  // Fallback: Copy to clipboard and show toast
  try {
    await navigator.clipboard.writeText(url);
    // Call onSuccess to show toast when clipboard is used
    onSuccess?.();
  } catch (error: any) {
    // If clipboard API fails, try fallback method
    try {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      textArea.style.pointerEvents = 'none';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      // Call onSuccess to show toast when clipboard is used
      onSuccess?.();
    } catch (fallbackError: any) {
      onError?.(fallbackError);
    }
  }
}

